import ExcelJS from 'exceljs';
import { restarSeisHoras } from '../fechaMexico';
import {
  generarGraficaOcupacionGeneral,
  generarGraficaPorCategoria,
  generarGraficaTopMaterias,
  generarGraficaTokens,
  generarGraficaHorasPico,
} from './graficasDashboard';

const COLOR_ENCABEZADO = 'FF1F4E78'; // azul oscuro
const COLOR_FILA_PAR = 'FFEAF1F8'; // azul muy claro, para el efecto zebra
const COLOR_TARJETA = 'FFEAF1F8';

function crearHojaEstilizada(workbook, nombreHoja, filasObjetos, anchoPorClave = {}) {
  const sheet = workbook.addWorksheet(nombreHoja.slice(0, 31));
  const claves = filasObjetos.length > 0 ? Object.keys(filasObjetos[0]) : [];

  sheet.columns = claves.map((clave) => ({
    header: clave,
    key: clave,
    width: anchoPorClave[clave] ?? Math.max(10, clave.length + 2),
  }));

  filasObjetos.forEach((fila) => sheet.addRow(fila));

  const filaEncabezado = sheet.getRow(1);
  filaEncabezado.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ENCABEZADO } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  filaEncabezado.height = 20;

  for (let numFila = 2; numFila <= filasObjetos.length + 1; numFila += 1) {
    if (numFila % 2 === 0) {
      sheet.getRow(numFila).eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_FILA_PAR } };
      });
    }
  }

  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  if (claves.length > 0) {
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: filasObjetos.length + 1, column: claves.length },
    };
  }

  return sheet;
}

function insertarImagen(workbook, sheet, grafica, col, row) {
  const imageId = workbook.addImage({ base64: grafica.dataUrl, extension: 'png' });
  sheet.addImage(imageId, {
    tl: { col, row },
    ext: { width: grafica.width, height: grafica.height },
  });
}

function agregarTarjetaKpi(sheet, columna, fila, etiqueta, valor) {
  const celdaEtiqueta = sheet.getCell(fila, columna);
  celdaEtiqueta.value = etiqueta;
  celdaEtiqueta.font = { size: 9, color: { argb: 'FF64748B' } };
  celdaEtiqueta.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  celdaEtiqueta.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_TARJETA } };

  const celdaValor = sheet.getCell(fila + 1, columna);
  celdaValor.value = valor;
  celdaValor.font = { size: 14, bold: true, color: { argb: 'FF1F4E78' } };
  celdaValor.alignment = { horizontal: 'center', vertical: 'middle' };
  celdaValor.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_TARJETA } };
}

async function descargarWorkbook(workbook, nombreArchivo) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

/**
 * Genera un Excel con toda la información de los dashboards (resumen, categorías,
 * materias, alumnos registrados y tokens), más gráficas incrustadas en la hoja
 * de resumen. Todo se calcula del lado del cliente a partir de `datos`, la misma
 * respuesta que ya llena las tablas del dashboard (no hace ninguna llamada extra).
 */
export async function exportarReporteCompleto(datos, nombreArchivo = 'reporte_completo_registro.xlsx') {
  const workbook = new ExcelJS.Workbook();
  const { resumen, porCategoria, porMateria, registrados, tokens } = datos;

  // ---- Hoja "Resumen": título, tarjetas de KPI y gráficas ----
  const hojaResumen = workbook.addWorksheet('Resumen');
  for (let col = 1; col <= 8; col += 1) {
    hojaResumen.getColumn(col).width = 16;
  }

  hojaResumen.mergeCells(1, 1, 1, 8);
  const celdaTitulo = hojaResumen.getCell(1, 1);
  celdaTitulo.value = 'Reporte completo de registro — Registro LiFE CCM';
  celdaTitulo.font = { bold: true, size: 15, color: { argb: 'FFFFFFFF' } };
  celdaTitulo.alignment = { horizontal: 'center', vertical: 'middle' };
  celdaTitulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ENCABEZADO } };
  hojaResumen.getRow(1).height = 26;

  hojaResumen.mergeCells(2, 1, 2, 8);
  const celdaSubtitulo = hojaResumen.getCell(2, 1);
  celdaSubtitulo.value = `Generado el ${new Date().toLocaleString('es-MX')}`;
  celdaSubtitulo.font = { italic: true, size: 9, color: { argb: 'FF64748B' } };
  celdaSubtitulo.alignment = { horizontal: 'center' };

  const kpis = [
    ['Alumnos registrados', resumen.totalAlumnosRegistrados],
    ['Usuarios con permiso', resumen.totalUsuariosConPermiso],
    ['Pendientes de registrar', resumen.totalUsuariosPendientes],
    ['Lugares ocupados', `${resumen.lugaresOcupados} / ${resumen.capacidadTotal}`],
    ['Lugares libres', resumen.lugaresLibres],
    ['Ocupación general', `${resumen.porcentajeOcupacion}%`],
    ['Materias agotadas', resumen.materiasAgotadas],
    ['Claves generadas', tokens.total],
  ];
  hojaResumen.getRow(4).height = 26;
  hojaResumen.getRow(5).height = 20;
  kpis.forEach(([etiqueta, valor], i) => agregarTarjetaKpi(hojaResumen, i + 1, 4, etiqueta, valor));

  hojaResumen.mergeCells(7, 1, 7, 8);
  const celdaGraficas = hojaResumen.getCell(7, 1);
  celdaGraficas.value = 'Gráficas';
  celdaGraficas.font = { bold: true, size: 12, color: { argb: 'FF1F4E78' } };

  const filaGraficasArriba = 8;
  const filaGraficasAbajo = filaGraficasArriba + 16;
  const filaLineaTiempo = filaGraficasAbajo + 16;
  const colIzquierda = 0;
  const colDerecha = 9;

  insertarImagen(workbook, hojaResumen, generarGraficaOcupacionGeneral(resumen), colIzquierda, filaGraficasArriba);
  insertarImagen(workbook, hojaResumen, generarGraficaPorCategoria(porCategoria), colDerecha, filaGraficasArriba);
  insertarImagen(workbook, hojaResumen, generarGraficaTopMaterias(porMateria), colIzquierda, filaGraficasAbajo);
  insertarImagen(workbook, hojaResumen, generarGraficaTokens(tokens), colDerecha, filaGraficasAbajo);
  insertarImagen(workbook, hojaResumen, generarGraficaHorasPico(registrados), colIzquierda, filaLineaTiempo);

  hojaResumen.getRow(filaLineaTiempo + 14).height = 4;

  // ---- Hoja "Por categoría" ----
  const filasCategoria = porCategoria.map((c) => ({
    Categoría: c.category,
    '# Materias': c.materias,
    Ocupados: c.ocupados,
    Libres: c.libres,
    Capacidad: c.capacidad,
    'Ocupación (%)': c.porcentajeOcupacion,
  }));
  crearHojaEstilizada(workbook, 'Por categoría', filasCategoria, { Categoría: 24 });

  // ---- Hoja "Por materia" ----
  const filasMateria = porMateria.map((m) => ({
    Código: m.code,
    Nombre: m.name,
    Categoría: m.category,
    Ocupados: m.ocupados,
    Libres: m.libres,
    Capacidad: m.capacidad,
    'Ocupación (%)': m.porcentajeOcupacion,
    Estado: m.agotada ? 'Agotada' : 'Disponible',
  }));
  crearHojaEstilizada(workbook, 'Por materia', filasMateria, { Nombre: 28, Categoría: 16 });

  // ---- Hoja "Registrados" (todos, no solo los seleccionados) ----
  const filasRegistrados = registrados.map((r) => ({
    Email: r.email,
    'ID Materia': r.idMat,
    Código: r.materiaCode,
    Materia: r.materiaName,
    Categoría: r.materiaCategory,
    Registro: r.idRegistro,
    'Fecha de registro': restarSeisHoras(r.fechaRegistro) || '',
  }));
  crearHojaEstilizada(workbook, 'Registrados', filasRegistrados, {
    Email: 30,
    'ID Materia': 12,
    Materia: 24,
    'Fecha de registro': 18,
  });

  // ---- Hoja "Tokens" ----
  const filasTokens = tokens.detalle.map((t) => ({
    Token: t.token,
    'Creado por': t.creadoPor,
    Estado: t.usado ? 'Usado' : 'Disponible',
    'Usado por': t.usadoPor || '',
    'Fecha de uso': restarSeisHoras(t.fechaUso) || '',
  }));
  crearHojaEstilizada(workbook, 'Tokens', filasTokens, {
    'Creado por': 26,
    'Usado por': 26,
    'Fecha de uso': 18,
  });

  await descargarWorkbook(workbook, nombreArchivo);
}
